import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import TipTapTypography from '@tiptap/extension-typography';
import Youtube from '@tiptap/extension-youtube';
import { Dropcursor, Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Card, Col, Row, Space } from 'antd';
import debounce from 'lodash.debounce';
import { TbDots } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';
import ImageResize from 'tiptap-extension-resize-image';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import EditorPopover from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/04-EditorPopover';
import { Callout } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/Callout';
import Commands from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/Commands';
import commandsSuggestion from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/Commands/commandsSuggestion';
import LinkEmbed from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/LinkEmbed';
import { reportComponentService } from '@/services/database/reportComponents';
import { ReportComponentCommentButton, ReportComponentIcon, ReportComponentLockButton } from '@/shared';
import { DragHandle } from '@/shared/components/drag-and-drop';
import { getDropIndicatorColor } from '@/shared/components/drag-and-drop/platformStyles';
import { ReportComponentActionsDropdown } from '@/shared/components/dropdowns';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { DescriptionConfig, ReportComponentConfig } from '@/types/api/modules/business-intelligence';
import {
    normalizeReportDescriptionConfiguration,
    resolveReportDescriptionBody,
    sanitizeReportDescriptionHtml,
} from '@/utilities';

import './index.css';

interface DescriptionReportComponentProps {
    component: ReportComponent;
    persistenceMode?: 'persist' | 'draft' | 'readonly';
}

const DescriptionReportComponent: FC<DescriptionReportComponentProps> = ({
    component,
    persistenceMode = 'persist',
}): JSX.Element => {
    const { token, resolvedThemeName } = useAntDesignTheme();
    const reportAccess = useReportComponentAccess(component);
    const replaceDraftConfiguration = useReportComponentStore((state) => state.replaceDraftConfiguration);

    const [hovered, setHovered] = useState(false);
    const configuration = component.configuration as Partial<DescriptionConfig> | undefined;
    const initialBody = useMemo(
        () => resolveReportDescriptionBody(configuration, component.description),
        [component.description, configuration],
    );
    const lastPersistedBodyRef = useRef(initialBody);

    useEffect(() => {
        lastPersistedBodyRef.current = initialBody;
    }, [initialBody]);

    const editable = persistenceMode !== 'readonly' && reportAccess.canUpdate && !component.locked;
    const showCanvasControls = persistenceMode === 'persist';

    const persistBody = useCallback(
        async (nextBody: string) => {
            const configurationForPersistence = normalizeReportDescriptionConfiguration(
                {
                    ...(component.configuration as Partial<DescriptionConfig> | undefined),
                    body: nextBody,
                },
                component.description,
            );

            if (configurationForPersistence.body === lastPersistedBodyRef.current) {
                return;
            }

            try {
                await reportComponentService.updateConfiguration(
                    component.id,
                    configurationForPersistence as ReportComponentConfig,
                );

                lastPersistedBodyRef.current = configurationForPersistence.body;
            } catch (error) {
                captureException(error);
                toast.error('Failed to save description');
            }
        },
        [component.configuration, component.description, component.id],
    );

    const persistBodyRef = useRef(persistBody);

    useEffect(() => {
        persistBodyRef.current = persistBody;
    }, [persistBody]);

    const debouncedPersistBody = useMemo(
        () => debounce((nextBody: string) => persistBodyRef.current(nextBody), 750),
        [],
    );

    useEffect(() => {
        return () => {
            debouncedPersistBody.flush();
            debouncedPersistBody.cancel();
        };
    }, [debouncedPersistBody]);

    const handleBodyChange = useCallback(
        (nextBody: string) => {
            const sanitizedBody = sanitizeReportDescriptionHtml(nextBody);
            const nextConfiguration = normalizeReportDescriptionConfiguration(
                {
                    ...(component.configuration as Partial<DescriptionConfig> | undefined),
                    body: sanitizedBody,
                },
                component.description,
            );

            if (persistenceMode === 'draft') {
                replaceDraftConfiguration(component.id, nextConfiguration);
                return;
            }

            if (persistenceMode === 'persist') {
                debouncedPersistBody(nextConfiguration.body);
            }
        },
        [
            component.configuration,
            component.description,
            component.id,
            debouncedPersistBody,
            persistenceMode,
            replaceDraftConfiguration,
        ],
    );
    const handleBodyChangeRef = useRef(handleBodyChange);

    useEffect(() => {
        handleBodyChangeRef.current = handleBodyChange;
    }, [handleBodyChange]);

    const editor = useEditor(
        {
            extensions: [
                StarterKit.configure({
                    document: false,
                    blockquote: false,
                    codeBlock: false,
                    dropcursor: false,
                    horizontalRule: false,
                    link: {
                        autolink: true,
                        openOnClick: !editable,
                        linkOnPaste: true,
                        HTMLAttributes: {
                            rel: 'noopener noreferrer',
                            target: null,
                        },
                    },
                    paragraph: false,
                    text: false,
                }),
                Document,
                Paragraph,
                Text,
                TextStyle,
                Color,
                Blockquote.extend({
                    addAttributes() {
                        return {
                            class: {
                                default: null,
                                parseHTML: (element) => element.getAttribute('class'),
                                renderHTML: (attributes) => ({ class: attributes.class }),
                            },
                        };
                    },
                }).configure({
                    HTMLAttributes: {
                        class: resolvedThemeName === 'dark' ? 'blockquote-dark' : 'blockquote-light',
                    },
                }),
                Highlight.configure({ multicolor: true }),
                CodeBlock,
                Callout.configure({
                    types: ['info', 'warning', 'tip', 'danger'],
                }),
                TaskList,
                TaskItem.configure({
                    nested: true,
                }),
                HorizontalRule.configure({
                    HTMLAttributes: {
                        class: resolvedThemeName === 'dark' ? 'horizontal-rule-dark' : 'horizontal-rule-light',
                    },
                }),
                new LinkEmbed(),
                Dropcursor.configure({
                    color: getDropIndicatorColor(token),
                }),
                Image,
                ImageResize,
                Youtube.configure({
                    inline: true,
                    modestBranding: true,
                    progressBarColor: 'white',
                    allowFullscreen: true,
                    origin: import.meta.env.PROD ? 'https://dashboard.countercyclical.io' : 'http://localhost:3000',
                    controls: true,
                }),
                Placeholder.configure({
                    placeholder: `Start Writing (Use '/' For Commands)...`,
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                TipTapTypography,
                Commands.configure({
                    suggestion: commandsSuggestion as any,
                }),
            ],
            content: initialBody,
            editable,
            editorProps: {
                attributes: {
                    spellcheck: 'false',
                },
                handleDOMEvents: {
                    keydown: (view, event) => {
                        const isEmptyLine =
                            view.state.selection.$from.parent.isTextblock &&
                            view.state.selection.$from.parent.textContent === '';

                        if (event.key === 'Enter' && isEmptyLine) {
                            editor?.chain().focus().insertContent('<br />').run();

                            return true;
                        }

                        return false;
                    },
                },
            },
            parseOptions: {
                preserveWhitespace: 'full',
            },
            onUpdate({ editor }) {
                handleBodyChangeRef.current(editor.getHTML());
            },
        },
        [resolvedThemeName, token.colorTextQuaternary],
    );

    useEffect(() => {
        if (!editor) {
            return;
        }

        editor.setEditable(editable);
    }, [editable, editor]);

    useEffect(() => {
        if (!editor || editor.isFocused || editor.getHTML() === initialBody) {
            return;
        }

        editor.commands.setContent(initialBody, { emitUpdate: false });
    }, [editor, initialBody]);

    const flushPendingSave = () => {
        if (persistenceMode === 'persist') {
            debouncedPersistBody.flush();
        }
    };

    return (
        <Card
            key={component.id}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            styles={{ body: { padding: 0 } }}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                borderRadius: token.borderRadius,
                overflow: 'hidden',
            }}
        >
            <Row style={{ padding: '0 0.5rem', height: '3rem' }} gutter={[8, 0]} align='middle' wrap={false}>
                {editable && showCanvasControls && (
                    <Col className='rgl-drag-handle-slot' flex='none'>
                        <DragHandle className='rgl-drag-handle' label={`Move ${component.name ?? 'description'}`} />
                    </Col>
                )}
                <Col flex='none'>
                    <ReportComponentIcon type={component.type} />
                </Col>
                <Col flex='0 1 auto' style={{ minWidth: 0 }}>
                    <strong
                        style={{
                            margin: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: '1px',
                        }}
                    >
                        {component.name ?? 'Untitled Component'}
                    </strong>
                </Col>
                <Col flex='auto' />
                {showCanvasControls && <ReportComponentLockButton component={component} />}
                {hovered && showCanvasControls && (
                    <Col flex='none'>
                        <Space.Compact>
                            {reportAccess.canComment && <ReportComponentCommentButton component={component} />}
                            <ReportComponentActionsDropdown component={component}>
                                <Button size='small'>
                                    <TbDots style={{ color: token.colorTextTertiary }} />
                                </Button>
                            </ReportComponentActionsDropdown>
                        </Space.Compact>
                    </Col>
                )}
            </Row>
            <div
                className='report-description-editor'
                onBlur={flushPendingSave}
                onMouseDown={(event) => event.stopPropagation()}
                style={{
                    padding: '0.5rem',
                    paddingTop: 0,
                    height: 'calc(100% - 3rem)',
                    color: token.colorText,
                    caretColor: token.colorTextSecondary,
                    backgroundColor: token.colorBgContainer,
                }}
            >
                {editor && editable && <EditorPopover editor={editor} />}
                <EditorContent editor={editor} inputMode='text' style={{ height: '100%' }} />
            </div>
        </Card>
    );
};

export default DescriptionReportComponent;
